import { useCallback, useEffect, useMemo, useState } from "react";
import { api, dataApi, authedUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import EmployeeCombobox from "@/components/EmployeeCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, Paperclip, Download, Upload, FileText, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { BranchSelect, BranchTag, useBranches } from "@/components/BranchBits";

const STATUSES = ["todo", "in_progress", "code_review", "testing", "completed"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const RANGES = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom range" },
];

// Local-timezone YYYY-MM-DD — using toISOString() would shift a day for any user
// east/west of UTC, which is why the "Today" filter was returning empty.
const isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Convert a UTC ISO string (as stored on tasks) into the viewer's local YYYY-MM-DD.
const toLocalDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : isoLocal(d);
};

function computeRange(key, custom) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  if (key === "today") return { from: isoLocal(now), to: isoLocal(now) };
  if (key === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: isoLocal(y), to: isoLocal(y) }; }
  if (key === "week") { const s = new Date(now); s.setDate(s.getDate() - now.getDay()); return { from: isoLocal(s), to: isoLocal(now) }; }
  if (key === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return { from: isoLocal(s), to: isoLocal(now) }; }
  if (key === "custom") return custom;
  return null;
}

function TaskForm({ open, onOpenChange, editing, projects, employees, onSaved, currentUserId, isAdmin }) {
  const [form, setForm] = useState({ title: "", description: "", project_id: "", assignee_id: "", priority: "medium", status: "todo", estimated_hours: 0, due_date: "", branch_id: "" });

  // Non-admins cannot assign to themselves — hide their own record from the picker.
  // Non-admins can only assign to members of the selected project.
  const assigneeOptions = useMemo(() => {
    if (isAdmin) return employees;
    const proj = projects.find((p) => p.id === form.project_id);
    const allowed = proj ? new Set([...(proj.member_ids || []), proj.manager_id]) : null;
    return employees.filter((e) => e.id !== currentUserId && (!allowed || allowed.has(e.id)));
  }, [employees, projects, form.project_id, isAdmin, currentUserId]);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        title: editing.title || "", description: editing.description || "", project_id: editing.project_id || "",
        assignee_id: editing.assignee_id || "", priority: editing.priority || "medium", status: editing.status || "todo",
        estimated_hours: editing.estimated_hours || 0, due_date: editing.due_date || "",
        branch_id: editing.branch_id || "",
      } : { title: "", description: "", project_id: "", assignee_id: "", priority: "medium", status: "todo", estimated_hours: 0, due_date: "", branch_id: "" });
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.title || !form.project_id) return toast.error("Title and project are required");
    try {
      const body = { ...form, branch_id: form.branch_id || null };
      if (editing) await api.patch(`/tasks/${editing.id}`, body);
      else await api.post("/tasks", body);
      toast.success(editing ? "Updated" : "Created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit Task" : "Create Task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="task-proj"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <BranchSelect value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} />
            <div>
              <Label>Assignee</Label>
              <EmployeeCombobox
                testid="task-assignee"
                employees={assigneeOptions}
                value={form.assignee_id}
                onChange={(v) => setForm({ ...form, assignee_id: v })}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Est. hrs</Label>
              <Input type="number" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: Number(e.target.value) })} />
            </div>
          </div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="task-save" onClick={save}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportTasksDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Pick a CSV file first");
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tasks/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} task${data.created === 1 ? "" : "s"}${data.failed ? `, ${data.failed} failed` : ""}`);
        onDone?.();
      } else {
        toast.error(`No tasks imported — ${data.failed} row${data.failed === 1 ? "" : "s"} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-import-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Tasks from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV with the columns below. <code className="text-xs">title</code> and{" "}
            <code className="text-xs">project_code</code> are required.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="w-3.5 h-3.5" />
            <span>Columns: title · description · project_code · assignee_email · priority · status · estimated_hours · due_date · tags · todos</span>
          </div>
          <a
            data-testid="task-import-template"
            href={authedUrl("/api/tasks/import/template.csv")}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <Download className="w-3.5 h-3.5" />Download blank template
          </a>

          <label
            htmlFor="task-import-file"
            className="block cursor-pointer glass rounded-xl border-2 border-dashed border-border/60 hover:border-primary/60 transition p-6 text-center"
          >
            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm font-medium">{file ? file.name : "Click to choose a .csv file"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Max ~5MB · comma-separated</div>
            <input
              id="task-import-file"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="task-import-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            />
          </label>

          {result && (
            <div className="rounded-xl border border-border/60 p-3 bg-background/60 max-h-64 overflow-auto" data-testid="task-import-results">
              <div className="flex items-center gap-4 text-sm font-medium mb-2">
                <span className="text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{result.created} created</span>
                {result.failed > 0 && <span className="text-red-500 inline-flex items-center gap-1"><XCircle className="w-4 h-4" />{result.failed} failed</span>}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-1 pr-2">Row</th>
                    <th className="pb-1 pr-2">Title</th>
                    <th className="pb-1 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.row} className="border-t border-border/40">
                      <td className="py-1 pr-2 number">{r.row}</td>
                      <td className="py-1 pr-2 truncate max-w-[260px]">{r.title || <span className="italic text-muted-foreground">—</span>}</td>
                      <td className={`py-1 pr-2 ${r.ok ? "text-emerald-500" : "text-red-500"}`}>
                        {r.ok ? "created" : (r.error || "failed")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>Close</Button>
          <Button data-testid="task-import-submit" onClick={submit} disabled={!file || uploading}>
            {uploading ? "Importing…" : "Import tasks"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Tasks() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCompanyWide = isAdmin || user?.role === "branch_admin"; // may assign to anyone in the company
  const { byId: branchById } = useBranches();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    setLoading(true);
    dataApi.tasks({}).then((r) => setTasks(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const projMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const rangeData = computeRange(range, { from: customFrom, to: customTo });
  const filtered = tasks
    .map((t) => ({
      ...t,
      _assignee_name: empMap[t.assignee_id]?.full_name || "Unassigned",
      _project_name: projMap[t.project_id]?.name || "",
      _reporter_name: empMap[t.reporter_id]?.full_name || "",
    }))
    .filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (rangeData?.from && rangeData?.to) {
        const created = toLocalDate(t.created_at);
        if (!created || created < rangeData.from || created > rangeData.to) return false;
      }
      return true;
    });

  const columns = [
    { key: "title", label: "Task", sortable: true, render: (r) => (
      <span className="font-medium flex items-center gap-1.5">
        {r.title}
        {(r.attachments || []).length > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" title={`${r.attachments.length} attachment(s)`}>
            <Paperclip className="w-3 h-3" />{r.attachments.length}
          </span>
        )}
      </span>
    )},
    { key: "project_id", label: "Project", sortable: true, render: (r) => projMap[r.project_id]?.name || "—" },
    { key: "assignee_id", label: "Assignee", sortable: true, render: (r) => empMap[r.assignee_id]?.full_name || "Unassigned" },
    { key: "priority", label: "Priority", sortable: true, render: (r) => <Badge variant="outline" className="capitalize text-[10px]">{r.priority}</Badge> },
    { key: "completion_percentage", label: "Progress", sortable: true, render: (r) => {
      const done = r.completed_todo_count ?? 0;
      const total = r.todo_count ?? 0;
      const pct = total ? Math.round((done / total) * 100) : 0;
      return (
        <div className="flex items-center gap-2 min-w-[120px]" data-testid={`progress-${r.id}`}>
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">{done}/{total}</span>
        </div>
      );
    } },
    { key: "status", label: "Status", sortable: true, render: (r) => (
      <Badge variant="secondary" className="capitalize text-[10px]" data-testid={`status-${r.id}`}>{r.status?.replace("_"," ")}</Badge>
    ) },
    { key: "due_date", label: "Due", sortable: true, hidden: false, className: "number" },
    { key: "branch_id", label: "Branch", sortable: true, hidden: false, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "created_at", label: "Created", sortable: true, hidden: true, className: "number", render: (r) => (r.created_at || "").slice(0, 10) },
    { key: "reporter_id", label: "Created By", sortable: true, hidden: true, render: (r) => empMap[r.reporter_id]?.full_name || "—" },
  ];

  const rowActions = useCallback((r) => {
    const isPM = projects.some((p) => p.id === r.project_id && p.manager_id === user?.id);
    const canEdit = r.can_modify ?? (isAdmin || isPM || r.reporter_id === user?.id);
    return (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" data-testid={`view-task-${r.id}`} onClick={() => nav(`/tasks/${r.id}`)}><Eye className="w-4 h-4" /></Button>
        {canEdit && (
          <>
            <Button variant="ghost" size="icon" data-testid={`edit-task-${r.id}`} onClick={() => { setEditing(r); setDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" data-testid={`del-task-${r.id}`} onClick={() => del(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </>
        )}
      </div>
    );
  }, [nav, isAdmin, user?.id, projects, branchById]);

  const isClient = user?.role === "client";

  return (
    <div className="space-y-6">
      <PageHeader
        testid="tasks-header" title="Tasks" subtitle={`${filtered.length} of ${tasks.length}`}
        actions={!isClient && (
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
              data-testid="export-tasks-btn"
              title="Download all visible tasks as CSV"
            >
              <a href={authedUrl("/api/tasks/export.csv")} rel="noopener">
                <Download className="w-4 h-4 mr-1" />Export CSV
              </a>
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                data-testid="import-tasks-btn"
                onClick={() => setImportOpen(true)}
                title="Bulk-create tasks from a CSV"
              >
                <Upload className="w-4 h-4 mr-1" />Import CSV
              </Button>
            )}
            <Button data-testid="new-task-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-1" />New Task
            </Button>
          </div>
        )}
      />
      <DataTable
        testid="dt-task"
        loading={loading}
        empty="No tasks"
        columns={columns}
        data={filtered}
        searchKeys={["title", "description", "_assignee_name", "_project_name", "_reporter_name"]}
        rowActions={rowActions}
        rowClassName={(r) => (r.reopen_count || 0) >= 1 ? "bg-orange-100/70 dark:bg-orange-950/40 hover:bg-orange-100 dark:hover:bg-orange-950/50" : ""}
        extraFilters={
          <>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36 h-9" data-testid="task-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-36 h-9" data-testid="task-range-filter"><SelectValue placeholder="Date range" /></SelectTrigger>
              <SelectContent>{RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {range === "custom" && (
              <>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-40" />
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-40" />
              </>
            )}
          </>
        }
      />
      <TaskForm open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} projects={projects} employees={employees} onSaved={load} currentUserId={user?.id} isAdmin={isCompanyWide} />
      <ImportTasksDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
